import React from 'react';
import { L } from '../router/Router';
import { C, F } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export const Btn = ({
  children,
  variant = 'primary',
  large = false,
  to,
  onClick,
  style = {},
  ...props
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: F,
    fontSize: large ? 'clamp(14px, 3.5vw, 18px)' : 'clamp(13px, 3vw, 16px)',
    fontWeight: 700,
    padding: large ? 'clamp(12px, 3vw, 16px) clamp(20px, 5vw, 32px)' : 'clamp(10px, 2.5vw, 12px) clamp(16px, 4vw, 24px)',
    borderRadius: '100px',
    cursor: 'pointer',
    border: 'none',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
    WebkitTapHighlightColor: 'transparent',
    ...style
  };

  const variants = {
    primary: {
      background: C.grad,
      color: C.white,
      boxShadow: '0 4px 20px rgba(0,130,251,0.3)'
    },
    secondary: {
      background: 'transparent',
      color: C.secondary,
      border: `2px solid ${C.secondary}`
    },
    outline: {
      background: isDark ? 'transparent' : 'rgba(0,0,0,0.03)',
      color: isDark ? C.white : C.black,
      border: isDark ? '2px solid rgba(255,255,255,0.3)' : '2px solid rgba(0,0,0,0.2)'
    }
  };

  // Enhanced hover effects with glow
  const getHoverStyle = () => {
    switch (variant) {
      case 'primary':
        return {
          y: -3,
          boxShadow: '0 8px 30px rgba(0,130,251,0.5), 0 0 20px rgba(0,130,251,0.3)'
        };
      case 'secondary':
        return {
          y: -3,
          boxShadow: `0 8px 30px ${C.secondary}40, 0 0 15px ${C.secondary}30`,
          background: `${C.secondary}10`
        };
      case 'outline':
        return isDark ? {
          y: -3,
          boxShadow: '0 8px 30px rgba(255,255,255,0.2), 0 0 15px rgba(255,255,255,0.1)',
          borderColor: 'rgba(255,255,255,0.6)',
          background: 'rgba(255,255,255,0.05)'
        } : {
          y: -3,
          boxShadow: '0 8px 30px rgba(0,130,251,0.2), 0 0 15px rgba(0,130,251,0.1)',
          borderColor: C.secondary,
          background: `${C.secondary}10`
        };
      default:
        return { y: -3 };
    }
  };

  const content = (
    <motion.button
      whileHover={getHoverStyle()}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
      style={{ ...baseStyle, ...variants[variant] }}
      onClick={onClick}
      data-testid={props['data-testid']}
      {...props}
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>{children}</span>
      <ArrowRight size={18} style={{ flexShrink: 0 }} />
    </motion.button>
  );

  if (to) {
    return <L to={to} style={{ textDecoration: 'none' }}>{content}</L>;
  }

  return content;
};

export const Crd = ({ children, glow = false, style = {}, ...props }) => {
  return (
    <motion.div
      whileHover={{ 
        y: -8, 
        borderColor: 'rgba(255,255,255,0.25)',
        boxShadow: glow 
          ? '0 20px 40px rgba(0,130,251,0.3), 0 0 30px rgba(0,130,251,0.2)' 
          : '0 16px 32px rgba(0,0,0,0.2)'
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '24px',
        padding: '32px',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: glow ? C.neon : 'none',
        cursor: 'pointer',
        ...style
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};